import React, { useState, useEffect, useRef } from 'react';
import { Send, Settings } from 'lucide-react';
import { Assistant } from '@/types';
import { gtmService } from '@/services/retargeting/gtm.service';
import { fbPixelService } from '@/services/retargeting/fb.pixel.service';

const getCookieConsent = (): string | null => {
    try {
        return localStorage.getItem('cookieConsent');
    } catch (error) {
        console.warn('localStorage not available:', error);
        return null;
    }
};

const HIGH_INTENT_KEYWORDS = [
    'buy', 'purchase', 'order', 'best',
    'finance', 'loan', 'credit', 'financing',
    'lease','insurance', 'refinance'
];

interface ChatInputProps {
    assistant: Assistant;
    onSendMessage: (message: string) => void;
    disabled?: boolean;
    showBanner: () => void;
}

export const ChatInput: React.FC<ChatInputProps> = ({
                                                        assistant,
                                                        onSendMessage,
                                                        disabled,
                                                        showBanner
                                                    }) => {
    const [input, setInput] = useState('');
    const [cookieConsent, setCookieConsent] = useState<string | null>(null);
    const [hasTrackedFirstMessage, setHasTrackedFirstMessage] = useState(false);
    const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    // Handle iOS keyboard - use focus/blur instead of visualViewport
    const handleFocus = () => {
        setIsKeyboardOpen(true);
        if (isIOS) {
            // On iOS, scroll the input into view after keyboard opens
            setTimeout(() => {
                textareaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 300);
        }
    };

    const handleBlur = () => {
        setIsKeyboardOpen(false);
        if (isIOS) {
            // Force iOS to recalculate layout after keyboard closes
            setTimeout(() => {
                window.scrollTo(0, window.scrollY);
                // Force a repaint
                if (containerRef.current) {
                    containerRef.current.style.transform = 'translateZ(0)';
                    requestAnimationFrame(() => {
                        if (containerRef.current) {
                            containerRef.current.style.transform = '';
                        }
                    });
                }
            }, 100);
        }
    };

    useEffect(() => {
        const checkConsent = () => {
            const consent = getCookieConsent();
            setCookieConsent(consent);
        };

        checkConsent();

        const handleStorageChange = () => {
            checkConsent();
        };

        window.addEventListener('storage', handleStorageChange);
        const interval = setInterval(checkConsent, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
        };
    }, []);

    // Auto-resize textarea based on content
    useEffect(() => {
        if (textareaRef.current) {
            // Reset height to auto to get the correct scrollHeight
            textareaRef.current.style.height = 'auto';
            // Set height based on scrollHeight, with a max height
            const maxHeight = 200; // Maximum height in pixels (about 8-10 lines)
            const newHeight = Math.min(textareaRef.current.scrollHeight, maxHeight);
            textareaRef.current.style.height = `${newHeight}px`;
        }
    }, [input]);

    const detectHighIntentKeywords = (message: string): string[] => {
        const lowerMessage = message.toLowerCase();
        const foundKeywords: string[] = [];

        HIGH_INTENT_KEYWORDS.forEach(keyword => {
            const regex = new RegExp(`\\b${keyword.replace(/\s+/g, '\\s+')}\\b`, 'i');
            if (regex.test(lowerMessage)) {
                foundKeywords.push(keyword);
            }
        });

        return foundKeywords;
    };

    const trackHighIntentLead = (message: string, keywords: string[]) => {
        if (keywords.length > 0) {
            // Track with Google Ads using GTM service
            gtmService.trackHighIntentLead(assistant.name);

            // Track with Facebook Pixel service
            fbPixelService.trackHighIntentWords(assistant.name);

            console.log('High Intent Lead tracked:', {
                assistant_name: assistant.name,
                keywords: keywords
            });
        }
    };

    const isChatDisabled = cookieConsent === 'declined' || disabled;
    const hasNotConsented = cookieConsent !== 'accepted';
    const isInputDisabled = cookieConsent === 'declined';

    const handleSubmit = () => {
        if (input.trim() && !isChatDisabled) {
            const message = input.trim();

            // Track first message sent in this page visit
            if (!hasTrackedFirstMessage) {
                // Track with Google Ads using GTM service
                gtmService.trackFirstMessage(assistant.name);

                // Track with Facebook Pixel service
                fbPixelService.trackChatSend(assistant.name);

                setHasTrackedFirstMessage(true);
            }

            // Track high intent keywords
            const highIntentKeywords = detectHighIntentKeywords(message);
            if (highIntentKeywords.length > 0) {
                trackHighIntentLead(message, highIntentKeywords);
            }

            onSendMessage(message);
            setInput('');
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey && !isChatDisabled) {
            e.preventDefault();
            handleSubmit();
        }
    };

    const getPlaceholderText = () => {
        if (cookieConsent === 'declined') {
            return 'Chat disabled - cookies declined';
        }
        if (hasNotConsented) {
            return 'Accept cookies to chat...';
        }
        return `Ask ${assistant.name} anything...`;
    };

    return (
        <div
            ref={containerRef}
            className="fixed left-0 right-0 bg-[#1a1a1a] z-40"
            style={{
                bottom: 0,
                paddingBottom: 'env(safe-area-inset-bottom)',
                // Prevent iOS from hiding this behind keyboard
                WebkitTransform: 'translate3d(0,0,0)',
                transform: 'translate3d(0,0,0)'
            }}
        >
            <div className="max-w-3xl mx-auto px-1.5 py-1.5 sm:px-3 sm:py-2 md:p-4">
                {cookieConsent === 'declined' && (
                    <div className="mb-2 sm:mb-3 p-2 sm:p-3 bg-red-900/50 border border-red-700 rounded-lg">
                        <div className="flex items-center justify-between gap-3">
                            <p className="text-red-300 text-xs sm:text-sm flex-1">
                                <strong>Chat Disabled:</strong> You've declined cookies. Please accept cookies to use the chat feature.
                            </p>
                            <button
                                onClick={showBanner}
                                className="bg-cyan-400 hover:bg-cyan-500 text-black font-semibold rounded-full transition-all duration-300 ease-in-out px-3 sm:px-4 py-1.5 sm:py-2 flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm whitespace-nowrap flex-shrink-0"
                            >
                                <Settings size={14} className="sm:w-4 sm:h-4" />
                                <span>Cookie Settings</span>
                            </button>
                        </div>
                    </div>
                )}

                <div className="flex flex-col gap-0.5 sm:gap-1 md:gap-2">
                    <div className="flex gap-1.5 sm:gap-2 items-end">
                        <textarea
                            ref={textareaRef}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyPress={handleKeyPress}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            placeholder={getPlaceholderText()}
                            rows={1}
                            className={`flex-1 min-w-0 px-2.5 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 text-sm sm:text-base border rounded-2xl focus:outline-none transition-all text-white placeholder-gray-300 bg-[#2d2d2d] border-gray-500 resize-none overflow-hidden ${
                                isInputDisabled
                                    ? 'opacity-60'
                                    : 'focus:ring-2 focus:ring-cyan-400 focus:border-transparent focus:bg-[#2d2d2d]'
                            }`}
                            disabled={isInputDisabled}
                            style={{ minHeight: '44px' }}
                        />
                        <button
                            onClick={handleSubmit}
                            disabled={isChatDisabled || !input.trim()}
                            className={`text-black font-semibold rounded-full transition-all duration-300 ease-in-out flex-shrink-0 flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 md:w-auto md:h-auto md:px-6 md:py-3 ${
                                isChatDisabled || !input.trim()
                                    ? 'bg-gray-500 cursor-not-allowed'
                                    : 'bg-cyan-400 hover:bg-cyan-500 hover:shadow-lg transform hover:scale-105'
                            }`}
                            style={{ minWidth: '40px', minHeight: '40px' }}
                        >
                            <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                    </div>
                    <div className="px-0.5 sm:px-1">
                        <p className="text-[9px] sm:text-[10px] md:text-xs text-gray-300 leading-tight">
                            GPTforme provides general information only and may make mistakes. It does not offer professional, medical, legal, or financial advice. Please consult a qualified expert before making decisions.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};